import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, input, output, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Dialog } from '@capacitor/dialog';
import { ModalController } from '@ionic/angular/standalone';
import { DatetimeChangeEventDetail, DatetimeHighlight, IonDatetimeCustomEvent } from '@ionic/core';
import { TranslateService } from '@ngx-translate/core';
import { CameraButtonComponent, ImageDialogComponent, fieldSorter } from '@scandium-oy/ngx-scandium';
import { addDays, addHours, addMilliseconds, addMonths, addWeeks, addYears, hoursToMilliseconds, startOfToday } from 'date-fns';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { filter, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { AppCommonModule } from 'src/app/common.module';
import { Contractor } from 'src/app/models/contractor.model';
import { SiteArea } from 'src/app/models/site-area.model';
import { Site } from 'src/app/models/site.model';
import { SubItem } from 'src/app/models/sub-item.model';
import { RoomTicket, TicketStatus } from 'src/app/models/ticket.model';
import { User } from 'src/app/models/user.model';
import { TranslateTicketPipe } from 'src/app/pipes/ticket-translate/ticket-translate.pipe';
import { SelectDialogComponent } from 'src/app/select-dialog/select.dialog';
import { AreasService } from 'src/app/services/areas.service';
import { ContractorsService } from 'src/app/services/contractors.service';
import { UsersService } from 'src/app/services/users.service';
import { WorkerTicketsService } from 'src/app/services/worker-tickets.service';
import { SelectLocationDialogComponent } from 'src/app/sites/edit-site/floors/select-location/select-location.dialog';
import { getUniqueGuid } from 'src/app/utility/list';
import { deepClone } from 'src/app/utility/object';
import { Roles, companyAdmins, companyManagers } from 'src/app/utility/role';
import { defaultRoom } from 'src/app/utility/room';
import { defaultTask, defaultTaskOnSave, maxImages } from 'src/app/utility/ticket';
import { getDateWithTimezone, timeArray, validateDates } from 'src/app/utility/time';
import { ScheduleTicketDialogComponent } from 'src/app/working-calendar/site/dialog/schedule/schedule-ticket.dialog';
import { AttachmentsDialogComponent } from '../../attachments/attachments.dialog';
import { CarouselImage } from '../../carousel/images/carousel-image.model';
import { CarouselImagesComponent } from '../../carousel/images/carousel-images.component';
import { SelectAreaDialogComponent } from '../../select-area/select-area.dialog';
import { SelectDayComponent } from '../../select-day/select-day.component';
import { SelectSiteButtonComponent } from '../../select-site/select-site.component';
import { SpeechToTextComponent } from '../../speech-to-text/speech-to-text.component';
import { TakeVideoComponent } from '../../take-video/take-video.component';

@Component({
  selector: 'app-new-adhoc',
  templateUrl: './new-adhoc.component.html',
  styleUrls: ['./new-adhoc.component.scss'],
  imports: [
    AppCommonModule,
    CommonModule,
    SelectSiteButtonComponent,
    CarouselImagesComponent,
    CameraButtonComponent,
    TakeVideoComponent,
    SpeechToTextComponent,
    SelectDayComponent,
  ],
})
export class NewAdhocComponent implements OnInit {
  private areasService = inject(AreasService);
  private contractorsService = inject(ContractorsService);
  private formBuilder = inject(FormBuilder);
  private modalCtrl = inject(ModalController);
  private translate = inject(TranslateService);
  private translateTicketPipe = inject(TranslateTicketPipe);
  private usersService = inject(UsersService);
  private workerTicketsService = inject(WorkerTicketsService);

  private selectedContractorS = signal<SubItem>(null);
  private site$ = new BehaviorSubject<Site>(null);

  site = input<Site>(null);
  sites = input<Site[]>(null);
  ticket = input<RoomTicket>(null);
  aiTask = input(false);

  saved = output<RoomTicket[]>();

  readonly defaultRoom = defaultRoom;
  readonly pickerColumns = timeArray;
  readonly maxImages = maxImages;
  readonly companyAdmins = companyAdmins;

  freeLimit = signal(false);
  formGroup: FormGroup;
  selectedSite = signal<Site>(null);
  selectedRooms = signal<SiteArea[]>([]);
  loading = signal(false);
  hasBlueprints = signal(false);
  images = signal<CarouselImage[]>([]);
  minDate = signal(getDateWithTimezone(startOfToday()));
  highlightedDates = signal<DatetimeHighlight[]>([]);
  selectedWorkers = signal<string[]>([]);

  canSelectWorker = computed(() => {
    const role = this.usersService.role();
    return ![Roles.worker].includes(role);
  });

  canManage = computed(() => {
    const user = this.usersService.currentUserS();
    const adminsAndManagers = [...companyAdmins, ...companyManagers];
    if (adminsAndManagers.includes(this.roleS())) {
      return true;
    }
    if (user.role === Roles.contractorAdmin) {
      const contractor = this.selectedSite()?.mainContractors.find((it) => it.guid === user.contractors[0]);
      return contractor?.canManage;
    }
    return false;
  });

  contractors$ = toObservable(this.selectedSite).pipe(
    filter((site) => site != null),
    map((site) => site.mainContractors),
    map((contractors) => [
      ...contractors.map((it) => ({ guid: it.guid, name: it.name })),
    ]),
    map((items) => items.filter((it) => it != null)),
    shareReplay(1),
  );

  repeatOptions = signal([
    {
      guid: 'daily',
      name: this.translate.instant('calendar.site.repeatOptions.daily'),
    },
    {
      guid: 'weekly',
      name: this.translate.instant('calendar.site.repeatOptions.weekly'),
    },
    {
      guid: 'every2Week',
      name: this.translate.instant('calendar.site.repeatOptions.every2Week'),
    },
    {
      guid: 'monthly',
      name: this.translate.instant('calendar.site.repeatOptions.monthly'),
    },
    {
      guid: 'yearly',
      name: this.translate.instant('calendar.site.repeatOptions.yearly'),
    },
  ]);

  selectedRepeatOption = signal<SubItem & { start: string; end: string; time?: string }>(null);

  roleS = computed(() => this.usersService.role());

  users$ = combineLatest([toObservable(this.selectedSite), toObservable(this.selectedContractorS)]).pipe(
    filter(([site, contractor]) => site != null && contractor != null),
    switchMap(([site, contractor]) => this.usersService.getList({ site: site.guid, role: Roles.worker, contractor: contractor.guid })),
    map((users) => users.map((user) => ({ guid: user.guid, name: user.displayName }))),
    map((users) => users.sort(fieldSorter(['name']))),
    shareReplay(1),
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private setForm(form?: any) {
    const currentUser = this.usersService.currentUserS();
    const ticket = this.ticket();
    const users = [];
    if (ticket != null) {
      users.push(...ticket.users?.map((u, index) => ({ guid: u, name: ticket.usernames[index] })) ?? []);
    }
    const initialStart = new Date();
    initialStart.setMinutes(0, 0, 0);
    const initialEnd = addHours(initialStart, 1);

    this.formGroup = this.formBuilder.group({
      name: [ticket?.plainName ?? null, Validators.required],
      start: [getDateWithTimezone(initialStart), [Validators.required, validateDates]],
      end: [getDateWithTimezone(initialEnd), [Validators.required, validateDates]],
      duration: ticket?.hours ?? 0,
      users: [currentUser.role === Roles.worker ? [currentUser] : users, Validators.nullValidator],
      state: [TicketStatus.created],
      images: [],
      videos: [],
      attachments: [[]],
      room: [null, this.selectedSite()?.commercial ? Validators.nullValidator : Validators.required],
      description: [ticket?.description ?? ''],
      date: [this.aiTask() ? ticket.deadline : null],
      schedule: [null],
      location: [null],
      contractor: [ticket?.contractor ?? null],
    });
    if (form) {
      this.formGroup.setValue(form);
    }
    if (this.aiTask()) {
      ticket.images?.map((img) => this.onUrl(img, 'image'));
      ticket.videos?.map((video) => this.onUrl(video, 'video'));
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private saveRoomTicket(item: any, area: SiteArea, deadline?: Date) {
    const hours = +(this.formGroup.value.duration ?? 0);
    item.name = item.name === defaultTask ? defaultTaskOnSave : item.name;

    const roomTicket: RoomTicket = {
      guid: item.guid,
      name: item.name,
      plainName: item.name,
      hours,
      status: TicketStatus.created,
      history: [this.workerTicketsService.createHistory(TicketStatus.created, undefined, item.images, item.videos)],
      users: item.users?.map((u) => u.guid) ?? [],
      usernames: item.users?.map((u) => u.name ?? u.displayName) ?? [],
      images: item.images,
      videos: item.videos,
      attachments: item.attachments,
      description: item.description,
      deadline: deadline ?? (item.date ? new Date(item.date) : null),
      creator: { guid: this.usersService.currentUserS()?.guid ?? null, name: this.usersService.currentUserS()?.displayName },
      schedule: item.schedule ?? null,
      location: item.location ?? null,
      site: this.selectedSite().guid,
      client: this.selectedSite().client,
      area: { guid: area?.guid ?? null, name: area?.name ?? null },
      repeatGuid: item.repeatGuid ?? null,
      repeatTime: item.repeatTime ?? false,
    };

    if (this.selectedContractorS()) {
      roomTicket.contractor = { guid: this.selectedContractorS().guid, name: this.selectedContractorS().name };
    } else if (this.contractorsService.contractorS() != null) {
      roomTicket.contractor = { guid: this.contractorsService.contractorS().guid, name: this.contractorsService.contractorS().name };
    }

    return roomTicket;
  }

  private getNewDate(id: string, current: Date) {
    switch (id) {
      case 'daily':
        return addDays(current, 1);
      case 'weekly':
        return addWeeks(current, 1);
      case 'every2Week':
        return addWeeks(current, 2);
      case 'monthly':
        return addMonths(current, 1);
      case 'yearly':
        return addYears(current, 1);
    }
  }

  private saveAdHocTicket() {
    const item = this.formGroup.value;
    const deadlines: Date[] = [];
    if (this.selectedRepeatOption() != null) {
      const id = this.selectedRepeatOption().guid;
      const start = new Date(this.selectedRepeatOption().start);
      const end = this.selectedRepeatOption().end ? new Date(this.selectedRepeatOption().end) : addMonths(start, 12);
      const time = this.selectedRepeatOption().time ? new Date(this.selectedRepeatOption().time) : null;
      let current = start;
      if (time) {
        current.setHours(time.getHours());
        current.setMinutes(time.getMinutes());
        item.repeatTime = true;
      }
      while (current <= end) {
        deadlines.push(current);
        current = this.getNewDate(id, current);
      }
    }
    const repeatGuid = deadlines.length > 0 ? getUniqueGuid(new Date().getTime()) : null;
    item.repeatGuid = repeatGuid;
    if (this.selectedRooms().length > 1) {
      if (deadlines.length > 0) {
        const roomTickets = this.selectedRooms().map((room) => {
          return deadlines.map((deadline) => {
            return this.saveRoomTicket(item, room, deadline);
          });
        });
        this.saved.emit(roomTickets.flat());
      } else {
        const roomTickets = this.selectedRooms().map((room) => {
          return this.saveRoomTicket(item, room);
        });
        this.saved.emit(roomTickets);
      }
    } else {
      if (deadlines.length > 0) {
        const roomTickets = deadlines.map((deadline) => {
          return this.saveRoomTicket(item, item.room, deadline);
        });
        this.saved.emit(roomTickets);
      } else {
        const roomTicket = this.saveRoomTicket(item, item.room);
        this.saved.emit([roomTicket]);
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private onSelect(field: string, value: any) {
    this.formGroup.get(field).setValue(value);
  }

  private onSelectContractor(item: SubItem) {
    if (item.guid == null) {
      this.contractorsService.inviteNewContractor(this.selectedSite()).then((contractor) => {
        this.onSelect('contractor', contractor);
        this.selectedContractorS.set({ guid: contractor.guid, name: contractor.name });
      });
    } else {
      this.onSelect('contractor', item);
      this.selectedContractorS.set(item);
    }
  }

  ngOnInit() {
    const site = this.site();
    if (site) {
      this.onSite(site);
    } else {
      this.setForm();
    }
  }

  onAttachments() {
    const items = this.formGroup.get('attachments').value;
    this.modalCtrl.create({ component: AttachmentsDialogComponent, componentProps: { items } }).then((m) => {
      m.present();

      m.onDidDismiss().then((data) => {
        if (data.data) {
          this.formGroup.get('attachments').setValue(data.data ?? []);
        }
      });
    });
  }

  onDateChange(date: string) {
    this.formGroup.get('date').setValue(date);
  }

  onDuration(value: number | string) {
    if (value === null) {
      Dialog.prompt({
        title: this.translate.instant('tickets.otherTime'),
        message: this.translate.instant('tickets.otherTimeText'),
      }).then((confirmValue) => {
        if (confirmValue) {
          this.onDuration(+confirmValue.value);
        }
      });
      return;
    }
    if (value == null) {
      value = 0;
    }
    const millis = hoursToMilliseconds(+value);
    const initialStart = new Date(this.formGroup.get('start').value);
    const end = addMilliseconds(initialStart, millis);
    this.formGroup.get('start').setValue(getDateWithTimezone(initialStart));
    this.formGroup.get('end').setValue(getDateWithTimezone(end));
    this.onSelect('duration', +value);
  }

  onSchedule() {
    this.modalCtrl.create({ component: ScheduleTicketDialogComponent }).then((m) => {
      m.present();
      m.onDidDismiss().then((data) => {
        if (data.data) {
          this.formGroup.get('schedule').setValue(data.data);
        }
      });
    });
  }

  onSite(site: Site) {
    if (site) {
      this.site$.next(site);
      this.selectedSite.set(site);
      this.setForm();
      if (this.contractorsService.contractorS() != null) {
        this.onSelectContractor(this.contractorsService.contractorSubItem());
      }
      const user = this.usersService.currentUserS();
      if ([Roles.resident].includes(user.role)) {
        this.areasService.getAccessAreas(site.guid).pipe(
          take(1),
        ).subscribe((areas) => {
          if (areas.length === 1) {
            this.formGroup.get('room').setValue(areas[0]);
          }
        });
      }
    }
  }

  selectContractor(items: SubItem[]) {
    const clearButton = this.formGroup.get('contractor').value != null;
    this.modalCtrl.create({ component: SelectDialogComponent<Contractor>, componentProps: { items, clearButton } }).then((m) => {
      m.present();

      m.onDidDismiss().then((data) => {
        if (data.data) {
          if (data.data.clear) {
            this.formGroup.get('contractor').setValue(null);
          } else {
            const siteGuid = this.site$.getValue().guid;
            if (data.data.sites != null && !data.data.sites.includes(siteGuid)) {
              const contractor = data.data;
              this.contractorsService.updateSite(contractor.guid, siteGuid)
                .pipe(take(1))
                .subscribe(() => this.onSelectContractor(data.data));
              return;
            }
            this.onSelectContractor(data.data);
          }
        }
      });
    });
  }

  selectRepeatOption() {
    const clearButton = this.selectedRepeatOption() != null;
    this.modalCtrl.create({ component: SelectDialogComponent<Contractor>, componentProps: { items: this.repeatOptions(), clearButton } }).then((m) => {
      m.present();

      m.onDidDismiss().then((data) => {
        if (data.data) {
          if (data.data.clear) {
            this.selectedRepeatOption.set(null);
          } else {
            data.data.start = getDateWithTimezone(startOfToday());
            this.selectedRepeatOption.set(data.data);
          }
        }
      });
    });
  }

  onRepeatTimeChange(event: IonDatetimeCustomEvent<DatetimeChangeEventDetail>) {
    this.selectedRepeatOption.update((val) => {
      val.time = event.detail.value as string;
      return val;
    });
  }

  onRepeatStartChange(value: string) {
    this.selectedRepeatOption.update((val) => {
      val.start = value;
      return val;
    });
  }

  onRepeatEndChange(value: string) {
    this.selectedRepeatOption.update((val) => {
      val.end = value;
      return val;
    });
  }

  selectRoom(site: Site) {
    const areas = this.selectedRooms() ?? [];
    this.modalCtrl.create({
      component: SelectAreaDialogComponent,
      componentProps: { site, areas },
      cssClass: ['modal-fullscreen'],
    }).then((m) => {
      m.present();

      m.onDidDismiss<SiteArea>().then((data) => {
        if (data.data) {
          const room = deepClone(data.data);
          this.selectedRooms.set([room]);
          this.formGroup.get('room').setValue(room);
        }
      });
    });
  }

  selectWorker(items: SubItem[]) {
    const clearButton = this.formGroup.get('users').value.length > 0;
    this.modalCtrl.create({ component: SelectDialogComponent<User>, componentProps: { items, clearButton, multiple: true } }).then((m) => {
      m.present();

      m.onDidDismiss().then((data) => {
        if (data.data) {
          if (data.data.clear) {
            this.formGroup.get('users').setValue([]);
          } else {
            this.formGroup.get('users').setValue(data.data);
          }
        }
      });
    });
  }

  onWorker(item: { guid: string; name: string }) {
    this.formGroup.get('users').setValue([item]);
    this.selectedWorkers.update((val) => [...val, item.name]);
  }

  onImage(item: CarouselImage) {
    this.modalCtrl.create({
      component: ImageDialogComponent,
      componentProps: { image: item.url, images: this.formGroup.get('images').value },
      cssClass: ['modal-fullscreen', 'transparent-modal'],
    }).then((modal) => {
      modal.present();
    });
  }

  onMic(text: string) {
    const prevText = this.formGroup.get('description').value as string;
    this.formGroup.get('description').setValue(prevText ? `${prevText} ${text}` : text);
  }

  onTask(tasks: string[]) {
    const clearButton = this.formGroup.get('name').value;
    const itemsPromise = tasks.map(async (name) => ({ name: await this.translateTicketPipe.transform(name), value: name }));
    Promise.all(itemsPromise).then((items) => {
      this.modalCtrl.create({
        component: SelectDialogComponent,
        componentProps: { items, clearButton },
      }).then((m) => {
        m.present();

        m.onDidDismiss().then((data) => {
          if (data.data) {
            let value = data.data.value;
            if (data.data.clear) {
              this.onSelect('name', defaultTask);
              return;
            }
            if (value === this.translate.instant('kanban.newItem')) {
              Dialog.prompt({
                title: this.translate.instant('kanban.newItem'),
                message: this.translate.instant('item.name'),
              }).then((dialogValue) => {
                if (dialogValue.value) {
                  this.formGroup.get('name').setValue(dialogValue.value);
                } else {
                  this.formGroup.get('name').setValue(defaultTask);
                }
              });
            } else {
              this.onSelect('name', value);
            }
          }
        });
      });
    });
  }

  onLocation() {
    this.modalCtrl.create({ component: SelectLocationDialogComponent, componentProps: { site: this.selectedSite() }, cssClass: ['modal-fullscreen'] }).then((m) => {
      m.present();

      m.onDidDismiss().then((data) => {
        if (data.data) {
          this.formGroup.get('location').setValue(data.data);
        }
      });
    });
  }

  onUrl(url: string, type: 'image' | 'video') {
    const items: string[] = this.formGroup.get(`${type}s`).value ?? [];
    items.push(url);
    this.formGroup.get(`${type}s`).setValue(items);
    const item: CarouselImage = { url, type };
    this.images.update((val) => [...val, item]);
  }

  save() {
    this.loading.set(true);
    this.saveAdHocTicket();
  }
}
